/**
 * Matches auth error message substrings (lowercased) to user-friendly German
 * messages. Provider-agnostic: keyed on the English text, not on error codes.
 */

/**
 * Error message constants for different authentication scenarios
 */
const AUTH_ERROR_MESSAGES = {
	// Login errors
	INVALID_CREDENTIALS: 'Benutzername oder Passwort falsch.',
	EMAIL_NOT_CONFIRMED: 'Die E-Mail Adresse muss zunächst bestätigt werden.',

	// Registration errors
	EMAIL_ALREADY_EXISTS: 'Diese E-Mail-Adresse ist bereits registriert.',
	WEAK_PASSWORD: 'Das Passwort ist zu schwach. Bitte verwenden Sie mindestens 6 Zeichen.',
	INVALID_EMAIL: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',

	// Password reset errors
	USER_NOT_FOUND: 'Es wurde kein Benutzer mit dieser E-Mail-Adresse gefunden.',
	SAME_PASSWORD: 'Das neue Passwort darf nicht mit dem alten übereinstimmen.',

	// Rate limiting
	TOO_MANY_REQUESTS: 'Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.',

	// Account status
	USER_DISABLED: 'Dieser Benutzer wurde deaktiviert. Bitte kontaktieren Sie den Support.',
	USER_BANNED: 'Dieser Benutzer wurde gesperrt. Bitte kontaktieren Sie den Support.',

	// Network/Server errors
	NETWORK_ERROR: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
	SERVER_ERROR: 'Serverfehler. Bitte versuchen Sie es später erneut.',

	// Session errors
	SESSION_EXPIRED: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
	INVALID_TOKEN: 'Ungültiger Authentifizierungstoken. Bitte melden Sie sich erneut an.',

	// Email verification
	VERIFICATION_FAILED: 'Die E-Mail-Verifizierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
	VERIFICATION_EXPIRED: 'Der Verifizierungslink ist abgelaufen. Bitte fordern Sie einen neuen an.',

	//Magic link errors
	MAGIC_LINK_EXPIRED: 'Der Zugriffslink ist abgelaufen. Bitte fordern Sie einen neuen an.',
	MAGIC_LINK_INVALID: 'Der Zugriffslink ist ungültig. Bitte fordern Sie einen neuen an.',

	// Generic fallback
	UNKNOWN_ERROR: 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
};

/**
 * Pattern-based error matchers for auth errors.
 * Evaluated in order — first match wins.
 */
const AUTH_ERROR_MATCHERS = [
	{
		match: (msg) =>
			// 'email not verified' is Better Auth's wording on unverified logins.
			msg.includes('email not verified') ||
			msg.includes('email not confirmed') ||
			msg.includes('confirm your email') ||
			msg.includes('email confirmation'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.EMAIL_NOT_CONFIRMED, needsVerification: true, canRetry: false },
	},
	{
		match: (msg) =>
			msg.includes('invalid login credentials') ||
			msg.includes('invalid email or password') ||
			msg.includes('invalid password') ||
			msg.includes('incorrect password') ||
			msg.includes('wrong password'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS, needsVerification: false, canRetry: true },
	},
	{
		match: (msg) =>
			msg.includes('already registered') ||
			msg.includes('user already exists') ||
			msg.includes('email already exists') ||
			msg.includes('duplicate email'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS, needsVerification: false, canRetry: false },
	},
	{
		match: (msg) =>
			msg.includes('password is too weak') ||
			msg.includes('password should be at least') ||
			msg.includes('weak password'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.WEAK_PASSWORD, needsVerification: false, canRetry: true },
	},
	{
		match: (msg) =>
			msg.includes('invalid email') ||
			msg.includes('email is invalid') ||
			msg.includes('unable to validate email'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.INVALID_EMAIL, needsVerification: false, canRetry: true },
	},
	{
		// For login context, show invalid credentials (security best practice)
		// For password reset, be more specific
		match: (msg) =>
			msg.includes('user not found') || msg.includes('no user found') || msg.includes('email not found'),
		result: (context) => ({
			errorMessage:
				context === 'reset' ? AUTH_ERROR_MESSAGES.USER_NOT_FOUND : AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS,
			needsVerification: false,
			canRetry: true,
		}),
	},
	{
		match: (msg) =>
			msg.includes('user disabled') || msg.includes('account disabled') || msg.includes('user is disabled'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.USER_DISABLED, needsVerification: false, canRetry: false },
	},
	{
		match: (msg) => msg.includes('user banned') || msg.includes('account banned') || msg.includes('user is banned'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.USER_BANNED, needsVerification: false, canRetry: false },
	},
	{
		match: (msg, status) =>
			msg.includes('too many requests') ||
			msg.includes('rate limit') ||
			msg.includes('rate_limit') ||
			status === 429,
		result: {
			errorMessage: AUTH_ERROR_MESSAGES.TOO_MANY_REQUESTS,
			needsVerification: false,
			canRetry: true,
			retryAfter: 60,
		},
	},
	{
		match: (msg) =>
			msg.includes('network') ||
			msg.includes('fetch failed') ||
			msg.includes('connection') ||
			msg.includes('timeout') ||
			!navigator.onLine,
		result: { errorMessage: AUTH_ERROR_MESSAGES.NETWORK_ERROR, needsVerification: false, canRetry: true },
	},
	{
		match: (msg, status) =>
			status >= 500 || msg.includes('internal server error') || msg.includes('service unavailable'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.SERVER_ERROR, needsVerification: false, canRetry: true },
	},
	{
		match: (msg) =>
			msg.includes('session expired') ||
			msg.includes('session not found') ||
			msg.includes('refresh_token_not_found'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.SESSION_EXPIRED, needsVerification: false, canRetry: false },
	},
	{
		match: (msg) => msg.includes('invalid token') || msg.includes('jwt') || msg.includes('malformed token'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.INVALID_TOKEN, needsVerification: false, canRetry: false },
	},
	{
		match: (msg) =>
			(msg.includes('verification') || msg.includes('verify')) &&
			(msg.includes('expired') || msg.includes('invalid')),
		result: { errorMessage: AUTH_ERROR_MESSAGES.VERIFICATION_EXPIRED, needsVerification: true, canRetry: false },
	},
	{
		match: (msg) => msg.includes('verification') || msg.includes('verify'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.VERIFICATION_FAILED, needsVerification: true, canRetry: false },
	},
	{
		match: (msg) => msg.includes('same password') || msg.includes('new password should be different'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.SAME_PASSWORD, needsVerification: false, canRetry: true },
	},
];

const DEFAULT_AUTH_RESULT = {
	errorMessage: AUTH_ERROR_MESSAGES.UNKNOWN_ERROR,
	needsVerification: false,
	canRetry: true,
};

export const getAuthErrorMessage = (error, context = 'login') => {
	if (!error) return { ...DEFAULT_AUTH_RESULT };

	const errorMsg = (error.message || error.msg || '').toLowerCase();
	const errorStatus = error.status || error.statusCode;

	for (const { match, result } of AUTH_ERROR_MATCHERS) {
		if (match(errorMsg, errorStatus)) {
			return typeof result === 'function' ? result(context) : { ...result };
		}
	}

	return { ...DEFAULT_AUTH_RESULT, originalError: error.message };
};

export const getLoginErrorMessage = (error) => {
	const result = getAuthErrorMessage(error, 'login');
	return {
		errorMessage: result.errorMessage,
		needsVerification: result.needsVerification,
	};
};

export const getRegistrationErrorMessage = (error) => {
	return getAuthErrorMessage(error, 'register');
};

export const getPasswordResetErrorMessage = (error) => {
	return getAuthErrorMessage(error, 'reset');
};

/**
 * Pattern-based error matchers specific to magic link flows.
 * Falls back to general auth error matching if no magic-link-specific match is found.
 */
const MAGIC_LINK_MATCHERS = [
	{
		match: (msg) => msg.includes('otp expired') || msg.includes('token expired') || msg.includes('link expired'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.MAGIC_LINK_EXPIRED, canRetry: true },
	},
	{
		match: (msg) => msg.includes('otp invalid') || msg.includes('token invalid') || msg.includes('invalid otp'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.MAGIC_LINK_INVALID, canRetry: true },
	},
	{
		match: (msg, status) =>
			msg.includes('too many requests') ||
			msg.includes('rate limit') ||
			msg.includes('rate_limit') ||
			msg.includes('email rate limit') ||
			status === 429,
		result: { errorMessage: AUTH_ERROR_MESSAGES.TOO_MANY_REQUESTS, canRetry: true, retryAfter: 60 },
	},
	{
		match: (msg) => msg.includes('invalid email') || msg.includes('email is invalid'),
		result: { errorMessage: AUTH_ERROR_MESSAGES.INVALID_EMAIL, canRetry: true },
	},
];

export const getMagicLinkErrorMessage = (error) => {
	if (!error) {
		return { errorMessage: AUTH_ERROR_MESSAGES.UNKNOWN_ERROR, canRetry: true };
	}

	const errorMsg = (error.message || error.msg || '').toLowerCase();
	const errorStatus = error.status || error.statusCode;

	for (const { match, result } of MAGIC_LINK_MATCHERS) {
		if (match(errorMsg, errorStatus)) {
			return { ...result };
		}
	}

	// Fallback to general error handler
	return getAuthErrorMessage(error, 'magic_link');
};

export { AUTH_ERROR_MESSAGES };
